import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { SyncBlockInstance } from '@atlaskit/editor-synced-block-provider/providers/types';

/**
 * Check if a synced block contains specific node types by traversing the ADF content
 */
export function syncBlockContainsNodes(
	syncBlock: SyncBlockInstance | undefined,
	nodeTypes: string[],
): string[] {
	if (!syncBlock?.data?.content) {
		return [];
	}

	if (nodeTypes.length === 0) {
		return [];
	}

	const nodeTypesSet = new Set(nodeTypes);
	const foundTypes = new Set<string>();
	const content = syncBlock.data.content;

	const checkNode = (node: ADFEntity | undefined): boolean => {
		if (!node || typeof node !== 'object') {
			return false;
		}

		// Check if this node matches one of the requested types
		if (
			typeof node.type === 'string' &&
			nodeTypesSet.has(node.type) &&
			!foundTypes.has(node.type)
		) {
			// node type found, add to found set and remove from search set
			foundTypes.add(node.type);
			nodeTypesSet.delete(node.type);

			// If all types have been found, we can stop searching
			if (nodeTypesSet.size === 0) {
				return true;
			}
		}

		// Recursively check all child content
		if (Array.isArray(node.content)) {
			// Return true to stop searching if all types have been found in children
			return node.content.some(checkNode);
		}
		return false;
	};

	content.some(checkNode);

	return Array.from(foundTypes);
}
